/**
 * The in-app SharePoint browser's data (F-DMS).
 *
 * The backend browses Microsoft Graph with the connection the user made in
 * Settings, so nothing opens outside PONS:
 *
 *   GET /integrations/onedrive/browse/home                       own OneDrive + followed sites
 *   GET /integrations/onedrive/browse/sites?q=                   site search
 *   GET /integrations/onedrive/browse/libraries?site_id=         a site's document libraries
 *   GET /integrations/onedrive/browse/items?drive_id=&item_id=&q=&page=
 *
 * Picked items are imported with `importExternalItems('onedrive', …)` like
 * any other external pick.
 */

import apis from '../../../utils/apis';
import { IntegrationNotConnectedError, isNotConnectedError } from './externalImport';
import { fileExtension } from './filesPickerTypes';

export interface SharePointSite {
  id: string;
  name: string;
  web_url?: string | null;
}

export interface SharePointLibrary {
  id: string;
  name: string;
}

export interface SharePointItem {
  id: string;
  drive_id: string;
  name: string;
  is_folder: boolean;
  size?: number | null;
  mime_type?: string | null;
  child_count?: number | null;
  modified?: string | null;
}

export interface SharePointHome {
  drive: { id: string; name: string } | null;
  sites: SharePointSite[];
  /** False when the connection cannot list sites (older connection, personal account). */
  sites_available: boolean;
}

export interface SharePointPage {
  items: SharePointItem[];
  next_page: string | null;
}

/** What the import accepts (`import_service.ALLOWED_EXTENSIONS` in the backend). */
export const SHAREPOINT_IMPORTABLE = ['pdf', 'docx', 'doc', 'txt', 'xlsx', 'pptx', 'md', 'rtf'];

export const isImportable = (name: string) => SHAREPOINT_IMPORTABLE.includes(fileExtension(name));

/** A browse call failed; `code` is the backend's (RECONNECT_REQUIRED, ACCESS_DENIED, …). */
export class SharePointBrowseError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'SharePointBrowseError';
  }
}

async function browse<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
  const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v));
  try {
    const response = await apis.get(`/integrations/onedrive/browse/${path}`, { params: clean });
    return response?.data?.data as T;
  } catch (error: any) {
    const data = error?.response?.data || {};
    if (data.code === 'NOT_CONNECTED' || (data.code !== 'RECONNECT_REQUIRED' && isNotConnectedError(error))) {
      throw new IntegrationNotConnectedError('onedrive');
    }
    throw new SharePointBrowseError(data.code || 'UNKNOWN', data.error || '');
  }
}

export const fetchSharePointHome = () => browse<SharePointHome>('home');

export const searchSharePointSites = async (q: string) =>
  (await browse<{ sites: SharePointSite[] }>('sites', { q })).sites || [];

export const fetchSharePointLibraries = async (siteId: string) =>
  (await browse<{ libraries: SharePointLibrary[] }>('libraries', { site_id: siteId })).libraries || [];

export const fetchSharePointItems = (driveId: string, opts: { itemId?: string; q?: string; page?: string } = {}) =>
  browse<SharePointPage>('items', { drive_id: driveId, item_id: opts.itemId, q: opts.q, page: opts.page });
